import type {
	IRegisterUrlSyncScopeInput,
	IUrlSyncAdapter,
	IUrlSyncCoordinator,
	IUrlSyncScopeRegistration,
	UrlSyncHistoryMode,
} from './types';

function buildRegistrationKey({ routeKey, scope }: IRegisterUrlSyncScopeInput): string {
	return `${routeKey}\u0000${scope}`;
}

export function isValidUrlSyncScope(scope: string): boolean {
	return /^[A-Za-z0-9_-]+$/.test(scope);
}

/**
 * Coordinates URL access across independently mounted grids that share one router.
 */
export function createUrlSyncCoordinator(adapter: IUrlSyncAdapter): IUrlSyncCoordinator {
	const registrations = new Set<string>();
	const listeners = new Set<() => void>();
	let unsubscribeAdapter: (() => void) | undefined;
	let isNotifying = false;

	const releaseAdapterSubscription = (): void => {
		if (listeners.size !== 0 || isNotifying || !unsubscribeAdapter) {
			return;
		}
		const release = unsubscribeAdapter;
		unsubscribeAdapter = undefined;
		release();
	};

	// After the current stack. A router re-render during a navigation drops every grid listener and
	// re-subscribes before this runs; releasing inside that stack makes history skip this listener.
	const scheduleAdapterRelease = (): void => {
		queueMicrotask(releaseAdapterSubscription);
	};

	const notifyListeners = (): void => {
		// A grid remounts synchronously in response and re-subscribes mid-iteration. Iterating a
		// snapshot keeps the new subscription (already hydrated from this URL) from being notified
		// for the same change, and the membership check skips one torn down along the way.
		isNotifying = true;
		try {
			Array.from(listeners).forEach(listener => {
				if (listeners.has(listener)) {
					listener();
				}
			});
		} finally {
			isNotifying = false;
			scheduleAdapterRelease();
		}
	};

	const ensureAdapterSubscription = (): void => {
		if (unsubscribeAdapter) {
			return;
		}
		if (adapter.subscribeToExternalChanges) {
			unsubscribeAdapter = adapter.subscribeToExternalChanges(notifyListeners);
			return;
		}
		window.addEventListener('popstate', notifyListeners);
		unsubscribeAdapter = () => window.removeEventListener('popstate', notifyListeners);
	};

	const subscribeToExternalChanges = (listener: () => void): (() => void) => {
		listeners.add(listener);
		ensureAdapterSubscription();

		return () => {
			listeners.delete(listener);
			if (!isNotifying) {
				scheduleAdapterRelease();
			}
		};
	};

	const setSearch = (search: string, options: { history: UrlSyncHistoryMode }): void => {
		adapter.setSearch(search, options);
	};

	const getSearch = (): string => adapter.getSearch();

	const register = (input: IRegisterUrlSyncScopeInput): IUrlSyncScopeRegistration => {
		const reject = (message: string): IUrlSyncScopeRegistration => {
			console.error(message);
			return { isAccepted: false, release: () => undefined };
		};

		if (!input.routeKey.trim()) {
			return reject('URL sync route key must not be empty');
		}
		if (!isValidUrlSyncScope(input.scope)) {
			return reject(
				`URL sync scope "${input.scope}" may only contain letters, numbers, underscores, and hyphens`
			);
		}

		const registrationKey = buildRegistrationKey(input);
		if (registrations.has(registrationKey)) {
			return reject(`Duplicate URL sync scope "${input.scope}" on route "${input.routeKey}"`);
		}
		registrations.add(registrationKey);

		return {
			isAccepted: true,
			release: () => {
				registrations.delete(registrationKey);
			},
		};
	};

	return {
		getSearch,
		setSearch,
		subscribeToExternalChanges,
		register,
	};
}

export function getUrlSyncParamPrefix(scope: string): string {
	if (!isValidUrlSyncScope(scope)) {
		throw new Error('URL sync scope may only contain letters, numbers, underscores, and hyphens');
	}
	return `${scope}.`;
}
